import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { lexicalToHtml } from '../utils/lexicalToHtml';

interface AutoTextCarouselProps {
  richContent?: any;
  plainContent?: string;
  language: string;
  width: number;
  height: number;
  style: {
    fontSize: number;
    fontFamily: string;
    color: string;
    textAlign: string;
    fontWeight?: string | number;
    lineHeight?: number;
    letterSpacing?: number;
    textShadow?: string;
    backgroundColor?: string;
    borderColor?: string;
    borderWidth?: number;
    borderRadius?: number;
    padding?: number;
  };
  autoFitHeight?: boolean;
  fontSizeAuto?: boolean;
  fontSizeMin?: number;
  fontSizeMax?: number;
  onCarouselStateChange?: (hasMore: boolean) => void;
  verticalAlign?: 'top' | 'middle' | 'bottom';
}

export interface AutoTextCarouselRef {
  advanceCarousel: () => boolean;
  isAtEnd: () => boolean;
}

const ARROW_HEIGHT = 36;

const AutoTextCarousel = forwardRef<AutoTextCarouselRef, AutoTextCarouselProps>(({
  richContent,
  plainContent,
  language,
  width,
  height,
  style,
  autoFitHeight,
  fontSizeAuto,
  fontSizeMin = 18,
  fontSizeMax = 32,
  onCarouselStateChange,
  verticalAlign = 'top',
}, ref) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [computedFontSize, setComputedFontSize] = useState(style.fontSize);
  const [pageHeight, setPageHeight] = useState(0);
  const [measuredContentHeight, setMeasuredContentHeight] = useState(height);
  const measureRef = useRef<HTMLDivElement>(null);

  const getHtml = useCallback(() => {
    if (richContent) {
      const langContent = richContent[language] || richContent.fr;
      if (langContent) return lexicalToHtml(langContent);
    }
    if (plainContent) return `<p>${plainContent}</p>`;
    return '';
  }, [richContent, plainContent, language]);

  const html = getHtml();
  const padding = style.padding || 0;
  const innerWidth = width - padding * 2;
  const totalInnerHeight = height - padding * 2;

  // Combined: auto font size + page calculation
  useEffect(() => {
    if (!measureRef.current) return;
    const measure = measureRef.current;
    measure.innerHTML = html;
    measure.style.width = `${innerWidth}px`;
    measure.style.fontFamily = style.fontFamily;
    measure.style.lineHeight = `${style.lineHeight || 1.5}`;
    measure.style.letterSpacing = style.letterSpacing ? `${style.letterSpacing}px` : 'normal';

    // Step 1: Auto font size
    let fontSize = style.fontSize;
    if (fontSizeAuto) {
      fontSize = fontSizeMin;
      for (let size = fontSizeMax; size >= fontSizeMin; size -= 1) {
        measure.style.fontSize = `${size}px`;
        if (measure.scrollHeight <= totalInnerHeight) {
          fontSize = size;
          break;
        }
      }
    }
    setComputedFontSize(fontSize);

    // Step 2: Calculate pages based on whole lines only (no truncated lines)
    measure.style.fontSize = `${fontSize}px`;
    const contentHeight = measure.scrollHeight;
    const lineH = fontSize * (style.lineHeight || 1.5);
    const rawTextZone = totalInnerHeight - ARROW_HEIGHT * 2;
    const linesPerPage = Math.max(1, Math.floor(rawTextZone / lineH));
    const pageH = linesPerPage * lineH; // exact height = whole lines only
    const totalLines = Math.ceil(contentHeight / lineH);
    const rawPages = Math.ceil(totalLines / linesPerPage);
    const needsCarousel = contentHeight > totalInnerHeight && rawPages > 1;
    const pages = needsCarousel ? Math.max(1, rawPages) : 1;
    setPageHeight(needsCarousel ? pageH : totalInnerHeight);
    setMeasuredContentHeight(measure.scrollHeight + padding * 2);
    setTotalPages(pages);
    if (currentPage >= pages) setCurrentPage(Math.max(0, pages - 1));
    onCarouselStateChange?.(pages > 1 && currentPage < pages - 1);
  }, [html, innerWidth, totalInnerHeight, fontSizeAuto, fontSizeMin, fontSizeMax, style.fontFamily, style.lineHeight, style.letterSpacing, style.fontSize, currentPage]);

  useImperativeHandle(ref, () => ({
    advanceCarousel: () => {
      if (currentPage < totalPages - 1) { setCurrentPage(p => p + 1); return true; }
      return false;
    },
    isAtEnd: () => currentPage >= totalPages - 1,
  }), [currentPage, totalPages]);

  const goUp = () => { if (currentPage > 0) setCurrentPage(p => p - 1); };
  const goDown = () => { if (currentPage < totalPages - 1) setCurrentPage(p => p + 1); };

  const touchStartY = useRef(0);
  const handleTouchStart = (e: React.TouchEvent) => { touchStartY.current = e.touches[0].clientY; };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const d = touchStartY.current - e.changedTouches[0].clientY;
    if (d > 40) goDown();
    if (d < -40) goUp();
  };

  const hasMore = totalPages > 1 && currentPage < totalPages - 1;
  const hasPrev = currentPage > 0;
  const isCarousel = totalPages > 1;

  // L'espace restant entre pageHeight arrondi et la zone disponible, réparti en padding
  const availableForText = isCarousel ? totalInnerHeight - ARROW_HEIGHT * 2 : totalInnerHeight;
  const extraSpace = isCarousel ? Math.max(0, availableForText - pageHeight) : 0;
  const textPaddingTop = Math.floor(extraSpace / 2);
  const textPaddingBottom = extraSpace - textPaddingTop;

  // Si auto-fit et pas de carousel : réduire à la taille du contenu
  const actualHeight = (autoFitHeight && !isCarousel)
    ? Math.min(height, measuredContentHeight)
    : height;

  const arrowStyle: React.CSSProperties = {
    height: ARROW_HEIGHT,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: style.color,
    fontFamily: 'sans-serif',
    fontSize: 14,
    lineHeight: 1,
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    width: '100%',
    userSelect: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        width,
        height: actualHeight,
        position: 'relative',
        backgroundColor: style.backgroundColor || undefined,
        borderColor: style.borderColor || undefined,
        borderWidth: style.borderWidth || undefined,
        borderStyle: style.borderWidth ? 'solid' : undefined,
        borderRadius: style.borderRadius || undefined,
        overflow: 'hidden',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        padding,
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Hidden measure div */}
      <div ref={measureRef} className="rich-text-content" style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', width: innerWidth, left: -9999, textWrap: 'balance' as any, fontWeight: style.fontWeight || 'normal' }} />

      {/* Arrow up zone */}
      {isCarousel && (
        <button
          onClick={(e) => { e.stopPropagation(); goUp(); }}
          style={{
            ...arrowStyle,
            opacity: hasPrev ? 1 : 0.2,
            pointerEvents: hasPrev ? 'auto' : 'none',
          }}
        >
          &#x25B2;
        </button>
      )}

      {/* Text zone */}
      <div style={{
        flex: isCarousel ? undefined : 1,
        height: isCarousel ? pageHeight : undefined,
        marginTop: textPaddingTop,
        marginBottom: textPaddingBottom,
        overflow: 'hidden',
        position: 'relative',
        display: !isCarousel ? 'flex' : undefined,
        flexDirection: !isCarousel ? 'column' : undefined,
        justifyContent: !isCarousel ? (verticalAlign === 'bottom' ? 'flex-end' : verticalAlign === 'middle' ? 'center' : 'flex-start') : undefined,
      }}>
        <div
          style={{
            transform: `translateY(-${currentPage * pageHeight}px)`,
            transition: 'transform 0.3s ease-out',
            fontSize: computedFontSize,
            fontFamily: style.fontFamily,
            color: style.color,
            textAlign: style.textAlign as any,
            fontWeight: style.fontWeight || 'normal',
            lineHeight: style.lineHeight || 1.5,
            letterSpacing: style.letterSpacing ? `${style.letterSpacing}px` : undefined,
            textShadow: style.textShadow || undefined,
            textWrap: 'balance' as any,
          }}
          className="rich-text-content"
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <style>{`
          .rich-text-content p { margin: 0; padding: 0; min-height: 1em; }
          .rich-text-content p + p { margin-top: 0.5em; }
        `}</style>
      </div>

      {/* Arrow down zone — clignotante tant qu'il y a du contenu en bas */}
      {isCarousel && (
        <button
          onClick={(e) => { e.stopPropagation(); goDown(); }}
          style={{
            ...arrowStyle,
            opacity: hasMore ? 1 : 0.2,
            pointerEvents: hasMore ? 'auto' : 'none',
            animation: hasMore ? 'carouselPulse 1.5s ease-in-out infinite' : 'none',
          }}
        >
          &#x25BC;
        </button>
      )}

      {/* Keyframe for pulse animation */}
      {isCarousel && hasMore && (
        <style>{`
          @keyframes carouselPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
        `}</style>
      )}
    </div>
  );
});

AutoTextCarousel.displayName = 'AutoTextCarousel';
export default AutoTextCarousel;
